import axios from "axios";
import { Loader2, MoreHorizontal, Printer } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import type { ColumnDef } from "@/components/fw/data-table/types";
import type { StorefrontOrderDto } from "@/lib/storefront-api";
import { storefrontSdk } from "../../storefrontsdk";
import { Button } from "../ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { StatusBadge } from "./StatusBadge";
import { TypeBadge } from "./TypeBadge";

// import { useStorefrontSettingsState } from "../../hooks/useStorefrontSettingsState";
// import { useInventoryState } from "../../hooks/useInventoryState";
// import { useCheckoutFormState } from "../../hooks/useCheckoutFormState";

type OrderRow = StorefrontOrderDto;

function formatDate(value?: string | null) {
	if (!value) {
		return "N/A";
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return new Intl.DateTimeFormat(undefined, {
		month: "short",
		day: "numeric",
		year: "numeric",
	}).format(date);
}

function formatCurrency(value?: string | null) {
	if (!value) {
		return "N/A";
	}

	const amount = Number(value);
	if (Number.isNaN(amount)) {
		return value;
	}

	return new Intl.NumberFormat(undefined, {
		style: "currency",
		currency: "USD",
	}).format(amount);
}

const getReportErrorMessage = (error: unknown) => {
	if (axios.isAxiosError(error)) {
		const data = error.response?.data;

		if (typeof data === "string" && data.trim()) {
			return data;
		}

		if (data && typeof data === "object") {
			const message = [
				(data as { message?: string }).message,
				(data as { Message?: string }).Message,
				(data as { title?: string }).title,
				(data as { error?: string }).error,
				(data as { detail?: string }).detail,
			].find((value) => typeof value === "string" && value.trim());

			if (message) {
				return message;
			}
		}

		if (error.response?.status) {
			return `Failed to generate report. Server returned ${error.response.status}.`;
		}
	}

	if (error instanceof Error && error.message) {
		return error.message;
	}

	return "Failed to generate report.";
};

const OrdersTableActions = ({ item }: { item: StorefrontOrderDto }) => {
	const [isPrinting, setIsPrinting] = useState(false);
	const [printError, setPrintError] = useState<string | null>(null);

	// const storefrontSettingsState = useStorefrontSettingsState();
	// const separateQuoteRequestsByCatalog =
	// 	storefrontSettingsState.get().settings?.SeparateQuoteRequestsByCatalog ??
	// 	false;
	// const inventoryState = useInventoryState();
	// const checkoutFormState = useCheckoutFormState();
	//-------------------------------------------------------------------------------
	const handlePrintOrder = async () => {
		if (!item.OrderId) {
			return;
		}

		setPrintError(null);
		setIsPrinting(true);

		try {
			let url = "";

			if (item.OrderType === "QUOTE") {
				url = (
					await storefrontSdk.storefrontV1RenderQuotePdfAsync(item.OrderId)
				).data;
			} else if (item.OrderType === "ORDER") {
				url = (
					await storefrontSdk.storefrontV1RenderOrderPdfAsync(item.OrderId)
				).data;
			}

			if (url && !window.open(url, "_blank")) {
				toast.warning("Please disable your popup blocker to see the PDF!", {
					duration: 5000,
					richColors: true,
				});
			}
		} catch (error) {
			setPrintError(getReportErrorMessage(error));
		} finally {
			setIsPrinting(false);
		}
	};
	//-------------------------------------------------------------------------------
	// const handleAddToCart = async () => {
	// 	const orderId = item.OrderId;
	// 	let webCatalogId = "";
	// 	if (separateQuoteRequestsByCatalog) {
	// 		webCatalogId = inventoryState.get().catalog?.Id ?? "";
	// 	}
	// 	const fromDate = checkoutFormState.getFromDate(webCatalogId);
	// 	const toDate = checkoutFormState.getToDate(webCatalogId);

	// 	if (!orderId) {
	// 		return;
	// 	}
	// 	const orderItems =
	// 		await storefrontSdk.apiV1StorefrontOrderToShoppingCartItemsPost(
	// 			orderId,
	// 			webCatalogId,
	// 			fromDate,
	// 			toDate,
	// 		);
	// 	const shoppingCartItems = orderItems.data;
	// 	const orderItemsJson = JSON.stringify(shoppingCartItems);
	// 	localStorage.setItem("storefront-shoppingcartitems", orderItemsJson);
	// 	window.location.reload();
	// };
	//-------------------------------------------------------------------------------
	return (
		<>
			<Dialog
				open={isPrinting || !!printError}
				onOpenChange={(open) => {
					if (!open && !isPrinting) {
						setPrintError(null);
					}
				}}
			>
				<DialogContent className="sm:max-w-md [&>button]:hidden">
					<DialogHeader>
						<DialogTitle>
							{isPrinting ? "Generating report" : "Unable to generate report"}
						</DialogTitle>
						<DialogDescription>
							{isPrinting
								? "Please wait while we prepare your PDF."
								: printError}
						</DialogDescription>
					</DialogHeader>
					{isPrinting ? (
						<div className="flex items-center justify-center py-6">
							<Loader2 className="h-8 w-8 animate-spin text-primary" />
						</div>
					) : (
						<div className="flex justify-end pt-2">
							<Button type="button" onClick={() => setPrintError(null)}>
								Close
							</Button>
						</div>
					)}
				</DialogContent>
			</Dialog>

			<div className="flex justify-center">
				<DropdownMenu>
					<DropdownMenuTrigger
						render={<Button variant="ghost" className="h-8 w-8 p-0" />}
					>
						<span className="sr-only">Open menu</span>
						<MoreHorizontal className="h-4 w-4" />
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end">
						<DropdownMenuItem
							onClick={(e) => {
								e.stopPropagation();
								handlePrintOrder();
							}}
							disabled={!item.OrderId || isPrinting}
						>
							<Printer className="mr-2 h-4 w-4" />
							PDF / Print
						</DropdownMenuItem>
						{/* <DropdownMenuItem
							className="whitespace-nowrap"
							onClick={(e) => {
								e.stopPropagation();
								handleAddToCart();
							}}
							disabled={!item.OrderId}
						>
							<ShoppingCartIcon className="mr-2 h-4 w-4" />
							Add to Cart...
						</DropdownMenuItem> */}
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</>
	);
};

export const orderColumns: ColumnDef<OrderRow>[] = [
	{
		id: "OrderType",
		header: "Type",
		accessorKey: "OrderType",
		className: "whitespace-nowrap",
		cell: (order) => <TypeBadge type={order.OrderType ?? "?"} />,
		mobile: {
			order: 1,
		},
	},
	{
		id: "OrderDate",
		header: "Date",
		accessorKey: "OrderDate",
		cell: (order) => formatDate(order.OrderDate),
		className: "whitespace-nowrap",
		mobile: {
			order: 1,
		},
	},
	{
		id: "OrderNumber",
		header: "Number",
		accessorKey: "OrderNumber",
		className: "font-medium whitespace-nowrap",
		mobile: {
			order: 0,
			hideLabel: true,
			cell: (order) => (
				<div className="flex flex-wrap justify-between gap-2">
					<div>
						<div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
							Number
						</div>
						<div>{order.OrderNumber}</div>
					</div>
					<div>
						<div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
							Status
						</div>
						<div>
							<StatusBadge status={order.Status ?? "?"} />
						</div>
					</div>
				</div>
			),
		},
	},
	{
		id: "Deal",
		header: "Job",
		accessorKey: "Deal",
		className: "font-medium whitespace-nowrap",
		mobile: {
			order: 2,
			className: "font-medium text-base",
		},
	},
	{
		id: "Description",
		header: "Description",
		accessorKey: "Description",
		className: "min-w-[18rem]",
		mobile: {
			order: 3,
		},
	},
	{
		id: "OrderTotal",
		header: "Total",
		accessorKey: "OrderTotal",
		cell: (order) => formatCurrency(order.OrderTotal),
		className: "text-right whitespace-nowrap",
		headerClassName: "text-right",
		mobile: {
			order: 8,
			className: "font-medium",
		},
	},
	{
		id: "EstimatedStart",
		header: "Start Date",
		accessorKey: "EstimatedStart",
		cell: (order) => formatDate(order.EstimatedStart),
		className: "whitespace-nowrap",
		mobile: {
			order: 6,
			hideLabel: true,
			cell: (order) => (
				<div className="flex flex-wrap justify-between gap-2">
					<div>
						<div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
							Start
						</div>
						<div>{formatDate(order.EstimatedStart)}</div>
					</div>
					<div>
						<div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
							Stop
						</div>
						<div>{formatDate(order.EstimatedStop)}</div>
					</div>
				</div>
			),
		},
	},
	{
		id: "EstimatedStop",
		header: "Stop Date",
		accessorKey: "EstimatedStop",
		cell: (order) => formatDate(order.EstimatedStop),
		className: "whitespace-nowrap",
		mobile: {
			order: 7,
			hide: true,
		},
	},
	{
		id: "Warehouse",
		header: "Warehouse",
		accessorKey: "Warehouse",
		className: "whitespace-nowrap",
		mobile: {
			order: 5,
		},
	},
	{
		id: "Status",
		header: "Status",
		accessorKey: "Status",
		className: "whitespace-nowrap",
		cell: (order) => <StatusBadge status={order.Status ?? "?"} />,
		mobile: {
			order: 4,
			hide: true,
		},
	},

	{
		id: "actions",
		header: "",
		sortable: false,
		cell: (item) => <OrdersTableActions item={item} />,
		className:
			"sticky right-0 bg-accent shadow-[-8px_0_16px_-6px_rgba(0,0,0,0.1)] dark:shadow-[-8px_0_16px_-6px_rgba(0,0,0,0.3)]",
		headerClassName:
			"sticky right-0 bg-accent shadow-[-8px_0_16px_-6px_rgba(0,0,0,0.1)] dark:shadow-[-8px_0_16px_-6px_rgba(0,0,0,0.3)]",
		mobile: {
			hideLabel: true,
			order: 99,
			wrapperClassName: "pt-2",
			cell: (item) => <OrdersTableActions item={item} />,
		},
	},
];
